import { doc, setDoc, getDoc, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

export interface ThreadVisit {
  userId: string;
  threadCode: string;
  lastVisitAt: Timestamp;
}

const COLLECTION_NAME = 'thread_visits';

export async function updateThreadVisit(userId: string, threadCode: string): Promise<void> {
  if (!userId || !threadCode) return;

  const docId = `${userId}_${threadCode}`;
  const docRef = doc(db, COLLECTION_NAME, docId);

  try {
    await setDoc(docRef, {
      userId,
      threadCode,
      lastVisitAt: Timestamp.now()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating thread visit:', error);
  }
}

export async function getThreadVisit(userId: string, threadCode: string): Promise<Timestamp | null> {
  if (!userId || !threadCode) return null;

  const docId = `${userId}_${threadCode}`;
  const docRef = doc(db, COLLECTION_NAME, docId);

  try {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data() as ThreadVisit;
      return data.lastVisitAt;
    }
    return null;
  } catch (error) {
    console.error('Error getting thread visit:', error);
    return null;
  }
}

export async function getLastThreadVisit(userId: string, threadCode: string): Promise<Timestamp | null> {
  return getThreadVisit(userId, threadCode);
}

export async function getAllThreadVisits(userId: string): Promise<Record<string, Timestamp>> {
  if (!userId) return {};

  const visits: Record<string, Timestamp> = {};

  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(q);
    snapshot.forEach(doc => {
      const data = doc.data() as ThreadVisit;
      visits[data.threadCode] = data.lastVisitAt;
    });

    return visits;
  } catch (error) {
    console.error('Error getting all thread visits:', error);
    return {};
  }
}

export async function hasNewMessagesSinceLastVisit(
  threadCode: string,
  lastVisitAt: Timestamp | null,
  messagesCollection: any
): Promise<boolean> {
  try {
    if (!lastVisitAt) {
      const qAll = query(
        collection(db, 'messages'),
        where('thread_code', '==', threadCode),
        where('status', '==', 'active')
      );
      const allSnapshot = await getDocs(qAll);
      return !allSnapshot.empty;
    }

    const q = query(
      collection(db, 'messages'),
      where('thread_code', '==', threadCode),
      where('status', '==', 'active'),
      where('created_at', '>', lastVisitAt)
    );

    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking for new messages:', error);
    return false;
  }
}

export async function markAllThreadsAsRead(userId: string): Promise<void> {
  if (!userId) return;

  try {
    const threadsQuery = query(
      collection(db, 'messages'),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(threadsQuery);
    const uniqueThreads = new Set<string>();

    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.thread_code) {
        uniqueThreads.add(data.thread_code);
      }
    });

    const updatePromises = Array.from(uniqueThreads).map(threadCode =>
      updateThreadVisit(userId, threadCode)
    );

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking all threads as read:', error);
    throw error;
  }
}
